import { z } from 'zod';
import { phoneSchema } from '../utils/validation';

/**
 * User profile update schema (for self-update)
 *
 * Used by: PUT /profile
 */
export const UpdateUserProfileSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters').optional(),
  lastName: z.string().min(2, 'Last name must be at least 2 characters').optional(),
  phone: phoneSchema.optional(),
  avatar: z.string().url('Avatar must be a valid URL').optional(),
});

export type UpdateUserProfileRequest = z.infer<typeof UpdateUserProfileSchema>;

/**
 * Admin user creation schema
 *
 * Used by: POST /users (admin only)
 */
export const CreateUserAsAdminSchema = z.object({
  email: z.string().email('Invalid email format'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  role: z.enum(['CUSTOMER', 'VENDOR', 'VENDOR_STAFF', 'SUPER_ADMIN']),
  phone: phoneSchema.optional(),
});

export type CreateUserAsAdminRequest = z.infer<typeof CreateUserAsAdminSchema>;

/**
 * Admin user update schema
 *
 * Used by: PUT /users/:id (admin only)
 */
export const UpdateUserAsAdminSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters').optional(),
  lastName: z.string().min(2, 'Last name must be at least 2 characters').optional(),
  role: z.enum(['CUSTOMER', 'VENDOR', 'VENDOR_STAFF', 'SUPER_ADMIN']).optional(),
  isActive: z.boolean().optional(),
  phone: phoneSchema.optional(),
});

export type UpdateUserAsAdminRequest = z.infer<typeof UpdateUserAsAdminSchema>;

/**
 * User list query params schema
 *
 * Used by: GET /users (admin only)
 */
export const UserListQuerySchema = z.object({
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(20),
  role: z.enum(['CUSTOMER', 'VENDOR', 'VENDOR_STAFF', 'SUPER_ADMIN']).optional(),
  search: z.string().optional(),
  isActive: z.coerce.boolean().optional(),
});

export type UserListQuery = z.infer<typeof UserListQuerySchema>;
